import { FC, useMemo } from 'react';
import { styled } from '@mui/material/styles';

import Button from 'src/presentations/components/atom/button';
import Modal from '../../../molecules/modal/index.ts';
import { IModalUpdateBaseProps, ROW_ACTION_TYPE, TUpdateValues } from '../types.ts';
import { useInternalDashboardSelector } from '../Selector.tsx';
import { FormContainer } from '../../forms/FormContainer.tsx';
import { IModalProps } from '../../../molecules/modal/Modal.tsx';
import { StepLabel, Switch } from '@mui/material';
import { ViewMode } from './ViewMode';
import { StepIconProps } from '@mui/material/StepIcon';
import clsx from 'clsx';
import Step from '@mui/material/Step';
import { StyledStepper } from 'src/presentations/components/molecules/uploadInvoice/Upload.tsx';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types.ts';

interface IStyledModalProps extends IModalProps {
  size?: 'large' | 'medium';
}

const CustomModal = (props: IStyledModalProps) => <Modal {...props} />;

const steps = [
  { id: ValidateInvoiceStep.Step1_EditOCRData, label: 'Edit OCR Data' },
  { id: ValidateInvoiceStep.Step2_ReviewData, label: 'Review Data' },
];

export const StyledModal = styled(CustomModal)((props: IStyledModalProps) => ({
  zIndex: 999,

  '& .MuiDialog-paper': {
    maxWidth: props?.size === 'large' ? '1800px' : '540px',
    borderRadius: '16px',
    width: '80%',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
}));

const CustomStepIcon = (props: StepIconProps) => {
  const { active, completed, icon } = props;

  return (
    <span
      className={clsx('flex items-center justify-center w-36 h-36 text-20 font-bold rounded-full', {
        'bg-primary/25 text-primary': completed,
        'bg-primary text-white': active,
        'bg-white text-primary border border-neutral-20': !active && !completed,
      })}
    >
      {icon}
    </span>
  );
};

export const ModalValidateInvoiceBase: FC<IModalUpdateBaseProps> = (props) => {
  const { actionType, onSubmit, children, formContext, viewMode, setViewMode, isValidJson, onValidateForm, activeStep, setActiveStep } = props;
  const { isActionLoading, rowActionType, checkRowAction, stopRowAction } = useInternalDashboardSelector();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isOpen = useMemo(() => checkRowAction(actionType), [actionType, rowActionType]);

  const onSubmitForm = (values: TUpdateValues) => {
    if (activeStep === ValidateInvoiceStep.Step2_ReviewData) {
      onSubmit?.(values);
      stopRowAction();
      setActiveStep(ValidateInvoiceStep.Step1_EditOCRData);
    } else {
      onValidateForm?.(values);
      setActiveStep(ValidateInvoiceStep.Step2_ReviewData);
    }
  };

  return (
    <StyledModal
      open={isOpen}
      fullWidth
      onClose={() => {
        setActiveStep(ValidateInvoiceStep.Step1_EditOCRData);
        stopRowAction();
      }}
      size={
        actionType === ROW_ACTION_TYPE.EDIT_INVOICE || actionType === ROW_ACTION_TYPE.EDIT_OCR || actionType === ROW_ACTION_TYPE.VALIDATE_INVOICE
          ? 'large'
          : 'medium'
      }
    >
      <div>
        <div className='py-12 px-40 bg-neutral-10 rounded-8 mb-20 ml' style={{ width: '95%', margin: '20px auto 0 auto' }}>
          <StyledStepper activeStep={activeStep}>
            {steps.map(({ id, label }) => (
              <Step key={id}>
                <StepLabel color='inherit' StepIconComponent={CustomStepIcon}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </StyledStepper>
        </div>
        <div className='px-24 pb-24'>
          <FormContainer context={formContext} onSuccess={onSubmitForm}>
            <>{children}</>
            <div className='flex items-center justify-between mt-24 '>
              <Button
                variant='secondary'
                isFull={false}
                disabled={isActionLoading}
                onClick={() => {
                  setActiveStep(ValidateInvoiceStep.Step1_EditOCRData);
                  stopRowAction();
                }}
              >
                Close
              </Button>

              <div className='flex items-center gap-8'>
                <span>Json Mode</span>
                <Switch
                  checked={viewMode === ViewMode.RAW}
                  onChange={(e) => setViewMode(e.target.checked ? ViewMode.RAW : ViewMode.STRUCTURED)}
                  disabled={!isValidJson || isActionLoading}
                  sx={{
                    '& .MuiSwitch-track': {
                      borderRadius: 20,
                      backgroundColor: '#E0E0E0',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#00992B',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#fff',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked:hover': {
                      backgroundColor: '#00992B',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked:active': {
                      backgroundColor: '#00992B',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked:focus': {
                      boxShadow: '0 0 0 2px rgba(0, 153, 43, 1)',
                    },
                  }}
                />
                <Button type='submit' variant='primary' isFull={false} isLoading={isActionLoading} disabled={!isValidJson}>
                  {activeStep === ValidateInvoiceStep.Step1_EditOCRData ? 'Validate' : 'Confirm'}
                </Button>
              </div>
            </div>
          </FormContainer>
        </div>
      </div>
    </StyledModal>
  );
};
